import React, { useState, useEffect } from 'react';
import { DateTime } from 'luxon';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Box, Typography, Button, Avatar } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EditIcon from '@mui/icons-material/Edit';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AlarmIcon from '@mui/icons-material/Alarm';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import lookup from 'country-code-lookup';
import { TeamCrest } from './team-crest';
import { toast } from 'react-toastify';
import { useFetchEvent } from '../../hooks/fetch-event';
import { useAuth } from '../../hooks/useAuth';
import { placeBet, setResults } from '../../services/event-services';
import { getGroup } from '../../services/group-services';
import { CssTextField } from '../layout/elements';

/* ------------------------------------------------------------------ *
 * BWF · Event page — "Match Card" redesign (Direction 1)
 * Keeps the original data flow; restyles into branded, status-aware
 * match cards. Three states are derived from the data:
 *   upcoming  → kickoff is in the future (betting open, picks hidden)
 *   live      → kicked off, no result yet (picks revealed, betting closed)
 *   finished  → result set (winners highlighted)
 * ------------------------------------------------------------------ */

const ORANGE = '#ff9800';
const AMBER = '#ffc107';
const GREEN = '#43c463';
const LINE = '1px solid rgba(255,255,255,0.08)';
const LINE2 = '1px solid rgba(255,255,255,0.14)';

/* ----- styled primitives ----- */

const Page = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 18,
  textAlign: 'left',
});

const Card = styled(Box)(({ theme }) => ({
  background: theme.palette.background.paper,
  border: LINE,
  borderRadius: 16,
  padding: 16,
}));

const ScoreField = styled(CssTextField)({
  width: 78,
  '& .MuiInputBase-input': {
    fontSize: '1.9rem',
    fontWeight: 800,
    padding: '10px 0',
    fontFamily: '"Archivo", Inter, sans-serif',
  },
  '& .MuiOutlinedInput-root': { borderRadius: 12 },
});

const SectionLabel = styled(Typography)({
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  color: '#b0b0b0',
});

/* ----- helpers ----- */

const shortCode = (name) =>
  lookup.byCountry(name)?.iso3 || (name || '').slice(0, 3).toUpperCase();

const initials = (u) => (u?.username ? u.username.slice(0, 2).toUpperCase() : '??');

const avatarUrl = (u) => {
  const p = u?.profile?.image;
  if (!p) return null;
  return p.startsWith('http') ? p : `${process.env.REACT_APP_API_URL_BASE}${p}`;
};

function fmtCountdown(target, now) {
  if (!target || target <= now) return null;
  const d = target.diff(now, ['days', 'hours', 'minutes', 'seconds']).toObject();
  const days = Math.floor(d.days || 0);
  const h = Math.floor(d.hours || 0);
  const m = Math.floor(d.minutes || 0);
  const s = Math.floor(d.seconds || 0);
  if (days > 0) return `${days}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m ${s}s`;
}

/* ----- small components ----- */


function StatusPill({ status }) {
  const map = {
    upcoming: { label: 'Upcoming', color: '#b0b0b0', dot: '#7a7a7a', live: false },
    live: { label: 'Awaiting result', color: ORANGE, dot: ORANGE, live: true },
    finished: { label: 'Full time', color: '#fff', dot: null, live: false },
  };
  const s = map[status];
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.4, py: '5px', borderRadius: 999,
      background: s.live ? 'rgba(255,152,0,0.14)' : 'rgba(255,255,255,0.06)',
      border: s.live ? '1px solid rgba(255,152,0,0.4)' : LINE2,
    }}>
      {s.dot && (
        <Box sx={{
          width: 7, height: 7, borderRadius: '50%', background: s.dot,
          animation: s.live ? 'bwfPulse 1.4s ease-in-out infinite' : 'none',
          '@keyframes bwfPulse': { '0%,100%': { opacity: 1, transform: 'scale(1)' }, '50%': { opacity: 0.35, transform: 'scale(0.7)' } },
        }} />
      )}
      <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: s.color }}>
        {s.label}
      </Typography>
    </Box>
  );
}

function MatchCard({ event, evtTime, status }) {
  const hasResult = status === 'finished';
  return (
    <Card sx={{ borderRadius: '20px', p: '20px 16px' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <TeamCrest name={event.team1} />
        <Box sx={{ textAlign: 'center', minWidth: 92, pt: 1 }}>
          {hasResult ? (
            <>
              <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 1, fontFamily: '"Archivo", sans-serif' }}>
                <Typography component="span" sx={{ fontSize: 46, fontWeight: 800, color: '#fff', lineHeight: 1 }}>{event.score1}</Typography>
                <Typography component="span" sx={{ fontSize: 28, fontWeight: 700, color: '#7a7a7a' }}>:</Typography>
                <Typography component="span" sx={{ fontSize: 46, fontWeight: 800, color: '#fff', lineHeight: 1 }}>{event.score2}</Typography>
              </Box>
              <Typography sx={{ fontSize: 11, color: '#b0b0b0', mt: 1, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase' }}>Final</Typography>
            </>
          ) : status === 'live' ? (
            <>
              <Typography sx={{ fontSize: 40, fontWeight: 800, color: '#7a7a7a', lineHeight: 1, fontFamily: '"Archivo", sans-serif' }}>– : –</Typography>
              <Typography sx={{ fontSize: 11, color: ORANGE, mt: 1, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase' }}>Awaiting result</Typography>
            </>
          ) : (
            <Typography sx={{ fontSize: 34, fontWeight: 800, color: '#7a7a7a', lineHeight: 1, fontFamily: '"Archivo", sans-serif' }}>VS</Typography>
          )}
        </Box>
        <TeamCrest name={event.team2} />
      </Box>

      <Box sx={{ mt: 2.25, pt: 1.75, borderTop: LINE, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, color: '#b0b0b0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <CalendarTodayIcon sx={{ fontSize: 16, color: ORANGE }} />
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{evtTime.toFormat('ccc, dd LLL')}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <AlarmIcon sx={{ fontSize: 16, color: ORANGE }} />
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{evtTime.toFormat('HH:mm')}</Typography>
        </Box>
      </Box>
    </Card>
  );
}

function ScoreInputs({ code1, code2, score1, score2, setScore1, setScore2 }) {
  const onChange = (setter) => (e) => {
    const v = e.target.value;
    if (v >= 0 || v === '') setter(v);
  };
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.75 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, color: ORANGE }}>{code1}</Typography>
        <ScoreField type="number" variant="outlined" value={score1} inputProps={{ min: 0 }} onChange={onChange(setScore1)} />
      </Box>
      <Typography sx={{ color: AMBER, fontSize: 22, fontWeight: 700, mt: 2.5 }}>:</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, color: ORANGE }}>{code2}</Typography>
        <ScoreField type="number" variant="outlined" value={score2} inputProps={{ min: 0 }} onChange={onChange(setScore2)} />
      </Box>
    </Box>
  );
}

/* ----- prediction split chart ----- */

function PredictionSplit({ event, status, code1, code2 }) {
  // Picks are hidden before kickoff — showing the split would leak them
  if (status === 'upcoming') return null;

  const bets = (event.bets || []).filter(b => b.score1 !== null && b.score2 !== null);
  const total = bets.length;
  if (total === 0) return null;

  const team1 = bets.filter(b => b.score1 > b.score2).length;
  const team2 = bets.filter(b => b.score1 < b.score2).length;
  const draw = total - team1 - team2;
  const pct = (n) => Math.round((n / total) * 100);

  const segments = [
    { count: team1, color: ORANGE },
    { count: draw, color: '#555' },
    { count: team2, color: GREEN },
  ].filter(s => s.count > 0);

  return (
    <Card>
      <SectionLabel sx={{ mb: 1.75 }}>Prediction split</SectionLabel>

      <Box sx={{ display: 'flex', height: 14, borderRadius: 999, overflow: 'hidden', gap: '2px' }}>
        {segments.map((s, i) => (
          <Box key={i} sx={{ width: `${(s.count / total) * 100}%`, background: s.color }} />
        ))}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5 }}>
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: ORANGE }}>{code1}</Typography>
          <Typography sx={{ fontSize: 12, color: '#b0b0b0', mt: '2px' }}>{pct(team1)}% · {team1} bet{team1 === 1 ? '' : 's'}</Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#999' }}>Draw</Typography>
          <Typography sx={{ fontSize: 12, color: '#b0b0b0', mt: '2px' }}>{pct(draw)}% · {draw} bet{draw === 1 ? '' : 's'}</Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: GREEN }}>{code2}</Typography>
          <Typography sx={{ fontSize: 12, color: '#b0b0b0', mt: '2px' }}>{pct(team2)}% · {team2} bet{team2 === 1 ? '' : 's'}</Typography>
        </Box>
      </Box>
    </Card>
  );
}

/* ----- bets list ----- */

function BetRow({ bet, status, you }) {
  const win = status === 'finished' && bet.points === 3;
  const hidden = status === 'upcoming';
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.5, p: '10px 12px', borderRadius: '12px',
      background: win ? 'rgba(255,152,0,0.10)' : (you ? 'rgba(255,255,255,0.05)' : '#1e1e1e'),
      border: win ? '1px solid rgba(255,152,0,0.4)' : (you ? LINE2 : LINE),
    }}>
      <Avatar src={avatarUrl(bet.user)} sx={{
        width: 34, height: 34, fontSize: 13, fontWeight: 700, bgcolor: 'rgba(255,255,255,0.12)',
        boxShadow: you ? `0 0 0 2px ${ORANGE}` : 'none',
      }}>{initials(bet.user)}</Avatar>

      <Typography sx={{ flex: 1, fontSize: 14, fontWeight: you ? 700 : 600, color: '#fff', display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {bet.user?.username}
        {win && <EmojiEventsIcon sx={{ fontSize: 17, color: AMBER }} />}
      </Typography>

      {hidden ? (
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#7a7a7a', letterSpacing: 3, whiteSpace: 'nowrap', filter: 'blur(0.3px)' }}>? : ?</Typography>
      ) : (
        <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#fff', fontFamily: '"Archivo", sans-serif', whiteSpace: 'nowrap' }}>
          {bet.score1} : {bet.score2}
        </Typography>
      )}

      {status === 'finished' && (
        <Box sx={{
          minWidth: 44, textAlign: 'center', fontSize: 12, fontWeight: 800, borderRadius: 999, py: '3px',
          color: win ? '#1a1207' : '#b0b0b0',
          background: win ? AMBER : 'transparent',
          border: win ? 'none' : LINE2,
        }}>{bet.points} pt{bet.points === 1 ? '' : 's'}</Box>
      )}
    </Box>
  );
}

function BetsList({ event, status, me }) {
  const hidden = status === 'upcoming';
  const bets = event.bets || [];
  const rows = status === 'finished'
    ? [...bets].sort((a, b) => (b.points || 0) - (a.points || 0))
    : bets;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', m: '4px 2px 12px' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>
          {hidden ? `${event.num_bets ?? bets.length} friends predicted` : 'Predictions'}
        </Typography>
        {hidden && (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: '#b0b0b0' }}>
            <LockIcon sx={{ fontSize: 14 }} />
            <Typography sx={{ fontSize: 11 }}>reveal at kickoff</Typography>
          </Box>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {rows.map((bet) => (
          <BetRow key={bet.id} bet={bet} status={status} you={bet.user?.id === me} />
        ))}
        {rows.length === 0 && (event.num_bets ?? 0) === 0 && (
          <Typography sx={{ fontSize: 13, color: '#7a7a7a', textAlign: 'center', py: 2 }}>
            {status === 'upcoming' ? 'Place your prediction before kickoff!' : 'No predictions were made.'}
          </Typography>
        )}
        {rows.length === 0 && (event.num_bets ?? 0) > 0 && hidden && (
          <Typography sx={{ fontSize: 13, color: '#7a7a7a', textAlign: 'center', py: 2 }}>
            Predictions are hidden until kickoff.
          </Typography>
        )}
      </Box>
    </Box>
  );
}

/* ----- prediction / admin blocks ----- */

function YourPrediction({ myBet, status }) {
  if (!myBet) return null;
  const win = status === 'finished' && myBet.points === 3;
  return (
    <Card sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      background: win ? 'rgba(255,152,0,0.08)' : '#1e1e1e',
      border: win ? '1px solid rgba(255,152,0,0.3)' : LINE,
    }}>
      <Box>
        <SectionLabel sx={{ whiteSpace: 'nowrap' }}>Your prediction</SectionLabel>
        <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#fff', mt: 0.5 }}>{myBet.score1} : {myBet.score2}</Typography>
      </Box>
      {status === 'finished' ? (
        <Box sx={{ textAlign: 'right' }}>
          <Typography sx={{ fontSize: 26, fontWeight: 900, color: win ? AMBER : '#fff', fontFamily: '"Archivo", sans-serif' }}>+{myBet.points}</Typography>
          {win && <Typography sx={{ fontSize: 11, fontWeight: 700, color: AMBER, letterSpacing: 0.6 }}>EXACT SCORE</Typography>}
        </Box>
      ) : (
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#b0b0b0', px: 1.25, py: 0.75, border: LINE2, borderRadius: 999 }}>Locked</Typography>
      )}
    </Card>
  );
}

/* ----- prev/next context pills ----- */

function EventNav({ prevEvent, nextEvent, onGo }) {
  if (!prevEvent && !nextEvent) return null;
  const label = (e) => e ? `${shortCode(e.team1)} vs ${shortCode(e.team2)}` : '';
  const pillSx = {
    flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.75,
    p: '9px 12px', borderRadius: '12px', border: LINE2, cursor: 'pointer',
    background: 'transparent', transition: 'border-color .15s ease, background .15s ease',
    '&:hover': { borderColor: 'rgba(255,152,0,0.5)', background: 'rgba(255,152,0,0.06)' },
  };
  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      {prevEvent ? (
        <Box onClick={() => onGo(prevEvent)} sx={pillSx}>
          <ChevronLeftIcon sx={{ fontSize: 20, color: ORANGE, flexShrink: 0 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 10, color: '#7a7a7a', lineHeight: 1.2 }}>Previous</Typography>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#fff', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {label(prevEvent)}
            </Typography>
          </Box>
        </Box>
      ) : <Box sx={{ flex: 1 }} />}

      {nextEvent ? (
        <Box onClick={() => onGo(nextEvent)} sx={{ ...pillSx, justifyContent: 'flex-end', textAlign: 'right' }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 10, color: '#7a7a7a', lineHeight: 1.2 }}>Next</Typography>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#fff', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {label(nextEvent)}
            </Typography>
          </Box>
          <ChevronRightIcon sx={{ fontSize: 20, color: ORANGE, flexShrink: 0 }} />
        </Box>
      ) : <Box sx={{ flex: 1 }} />}
    </Box>
  );
}

/* ------------------------------------------------------------------ */

export default function Event() {
  const { authData } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, loading, error] = useFetchEvent(authData.token, id);

  const [event, setEvent] = useState(null);
  const [evtTime, setEvtTime] = useState(null);
  const [isFuture, setIsFuture] = useState(false);
  const [score1, setScore1] = useState('');
  const [score2, setScore2] = useState('');
  const [editing, setEditing] = useState(false);
  const [now, setNow] = useState(DateTime.now());

  // Ordered, lightweight event list for prev/next, passed from the list/timeline.
  // Each item: { id, team1, team2 } — just enough for the context pills.
  const [navEvents, setNavEvents] = useState(location.state?.navEvents || null);

  useEffect(() => {
    setEvent(data);
    if (data?.time) {
      const format = "yyyy-MM-dd'T'HH:mm:ss'Z'";
      const parsedUtc = DateTime.fromFormat(data.time, format, { zone: 'utc' });
      const local = parsedUtc.toLocal();
      setEvtTime(local);
      setIsFuture(local > DateTime.now());
    }
  }, [data]);

  // Moving between events resets the betting inputs.
  useEffect(() => {
    setScore1('');
    setScore2('');
    setEditing(false);
  }, [id]);

  // Fallback for deep links / refresh: if we arrived without the ordered list
  // (e.g. opened the URL directly), fetch the group's events once to build it.
  useEffect(() => {
    if (navEvents || !event?.group) return;
    getGroup(event.group, authData.token)
      .then((g) => {
        if (g?.events) {
          const list = [...g.events]
            .sort((a, b) => new Date(a.time) - new Date(b.time))
            .map((e) => ({ id: e.id, team1: e.team1, team2: e.team2 }));
          setNavEvents(list);
        }
      })
      .catch(() => { });
  }, [event?.group, navEvents, authData.token]);

  const idx = navEvents ? navEvents.findIndex((e) => String(e.id) === String(id)) : -1;
  const prevEvent = idx > 0 ? navEvents[idx - 1] : null;
  const nextEvent = idx >= 0 && navEvents && idx < navEvents.length - 1 ? navEvents[idx + 1] : null;
  const goToEvent = (target) => {
    if (!target) return;
    navigate(`/event/${target.id}`, { state: { navEvents } });
  };

  // Live ticking clock for the countdown + status flip at kickoff.
  useEffect(() => {
    const t = setInterval(() => {
      const n = DateTime.now();
      setNow(n);
      if (evtTime) setIsFuture(evtTime > n);
    }, 1000);
    return () => clearInterval(t);
  }, [evtTime]);

  const me = authData?.user?.id;
  const hasResult = event && event.score1 >= 0 && event.score2 >= 0;
  const status = isFuture ? 'upcoming' : (hasResult ? 'finished' : 'live');
  const myBet = event?.bets?.find((b) => b.user?.id === me) || null;
  const code1 = event ? shortCode(event.team1) : '';
  const code2 = event ? shortCode(event.team2) : '';
  const countdown = fmtCountdown(evtTime, now);
  const canSubmit = score1 !== '' && score2 !== '';

  const sendBet = async () => {
    const bet = await placeBet(authData.token, { score1, score2, event: event.id });
    if (bet) {
      if (bet.new) {
        event.bets.push(bet.result);
      } else {
        const i = event.bets.findIndex((el) => el.user.id === bet.result.user.id);
        event.bets[i] = bet.result;
      }
      setEvent({ ...event });
      toast.success(bet.message);
      setScore1('');
      setScore2('');
      setEditing(false);
    }
  };

  const setScores = async () => {
    const eventData = await setResults(authData.token, { score1, score2, event: event.id });
    if (eventData) {
      setEvent(eventData);
      toast.success('Scores have been set');
      setScore1('');
      setScore2('');
    } else {
      toast.error('Scores could not be set');
    }
  };

  const startEdit = () => {
    if (myBet) { setScore1(String(myBet.score1)); setScore2(String(myBet.score2)); }
    setEditing(true);
  };

  if (error) return <Typography variant="h1" sx={{ textAlign: 'center', mt: 4 }}>Error</Typography>;
  if (loading || !event || !evtTime) return <Typography variant="h2" sx={{ textAlign: 'center', mt: 4 }}>Loading…</Typography>;

  const showBetForm = status === 'upcoming' && (!myBet || editing);
  const showAdminForm = status !== 'upcoming' && event.is_admin;

  return (
    <Page>
      {/* top bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Link to={`/details/${event.group}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: ORANGE, background: 'rgba(255,152,0,0.10)', border: '1px solid rgba(255,152,0,0.25)', borderRadius: 20, padding: '5px 12px 5px 6px', textDecoration: 'none', fontWeight: 600, fontSize: 13 }}>
          <ChevronLeftIcon sx={{ fontSize: 18 }} />
          Back
        </Link>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {event.is_admin && (
            <Link to={`/event-edit/${event.id}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#b0b0b0', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 20, padding: '5px 12px 5px 8px', textDecoration: 'none', fontWeight: 600, fontSize: 13 }}>
              <EditIcon sx={{ fontSize: 15 }} />
              Edit
            </Link>
          )}
          <StatusPill status={status} />
        </Box>
      </Box>

      <MatchCard event={event} evtTime={evtTime} status={status} />

      {/* prev/next navigation — sits between match details and prediction */}
      <EventNav prevEvent={prevEvent} nextEvent={nextEvent} onGo={goToEvent} />

      {/* upcoming · your prediction (form or locked-in confirmation) */}
      {status === 'upcoming' && (
        <Card>
          <SectionLabel sx={{ mb: 1.75 }}>Your prediction</SectionLabel>

          {showBetForm ? (
            <>
              <ScoreInputs code1={code1} code2={code2} score1={score1} score2={score2} setScore1={setScore1} setScore2={setScore2} />
              <Button fullWidth variant="contained" color="primary" disabled={!canSubmit} onClick={sendBet} sx={{ mt: 2, py: 1.1 }}>
                {myBet ? 'Update bet' : 'Place bet'}
              </Button>
              {countdown && (
                <Typography sx={{ textAlign: 'center', fontSize: 12, color: '#b0b0b0', mt: 1.25 }}>
                  Closes in {countdown}
                </Typography>
              )}
            </>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2.5, my: 0.5 }}>
                <Typography sx={{ fontSize: 40, fontWeight: 800, color: '#fff', fontFamily: '"Archivo", sans-serif', lineHeight: 1 }}>
                  {myBet.score1} <span style={{ color: AMBER }}>:</span> {myBet.score2}
                </Typography>
              </Box>
              <Box sx={{
                mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
                p: '11px 14px', borderRadius: '11px', background: 'rgba(67,196,99,0.10)', border: '1px solid rgba(67,196,99,0.32)',
              }}>
                <CheckCircleIcon sx={{ fontSize: 18, color: GREEN }} />
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>Locked in</Typography>
                {countdown && <Typography sx={{ fontSize: 13, color: '#b0b0b0' }}>· closes in {countdown}</Typography>}
              </Box>
              <Box sx={{ textAlign: 'center', mt: 1.25 }}>
                <Typography component="button" onClick={startEdit} sx={{
                  background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: ORANGE, fontWeight: 600, fontFamily: 'inherit',
                }}>Edit prediction</Typography>
              </Box>
            </>
          )}
        </Card>
      )}

      {/* live / finished · your prediction summary */}
      {status !== 'upcoming' && <YourPrediction myBet={myBet} status={status} />}

      {/* admin · set / edit result */}
      {showAdminForm && (
        <Card>
          <SectionLabel sx={{ mb: 1.75 }}>{hasResult ? 'Update result' : 'Set result'}</SectionLabel>
          <ScoreInputs code1={code1} code2={code2} score1={score1} score2={score2} setScore1={setScore1} setScore2={setScore2} />
          <Button fullWidth variant="contained" color="primary" disabled={!canSubmit} onClick={setScores} sx={{ mt: 2, py: 1.1 }}>
            {hasResult ? 'Update result' : 'Set result'}
          </Button>
        </Card>
      )}

      <PredictionSplit event={event} status={status} code1={code1} code2={code2} />

      <BetsList event={event} status={status} me={me} />
    </Page>
  );
}
